import type { EnemyDef } from '../../content/enemies/enemies';
import type { Element } from '../game/elements';
import type { CombatState, PlayerState } from '../game/types';
import { rollDodge, rollEnemySkillHit } from './accuracy';
import {
  actionBlockLabel,
  hasElementalImmunity,
  hasStatus,
  rollActionBlock,
} from './statuses';

export type EnemyAttackResult = {
  player: PlayerState;
  combat: CombatState;
  killed: boolean;
  dodged: boolean;
  missed: boolean;
  skipped: boolean;
};

/**
 * Enemy lethal action: action-block → accuracy → dodge → elemental immunity/invulnerable.
 * One-hit rule when an unblocked attack lands.
 */
export function resolveEnemyAttack(
  player: PlayerState,
  combat: CombatState,
  enemy: Pick<EnemyDef, 'accuracy' | 'name'> & { element?: Element } = {
    accuracy: combat.enemyAccuracy,
    name: combat.enemyName,
    element: 'physical',
  },
  rng: () => number = Math.random,
): EnemyAttackResult {
  if (!player.alive || combat.enemyHp <= 0) {
    return {
      player,
      combat,
      killed: false,
      dodged: false,
      missed: false,
      skipped: false,
    };
  }

  const blocked = rollActionBlock(combat.enemyStatuses, rng);
  if (blocked) {
    const line = `${enemy.name} ${actionBlockLabel(blocked)} — bỏ lượt!`;
    return {
      player,
      combat: { ...combat, log: [line, ...combat.log].slice(0, 8) },
      killed: false,
      dodged: false,
      missed: false,
      skipped: true,
    };
  }

  if (!rollEnemySkillHit(enemy.accuracy, rng)) {
    const line = `${enemy.name} đánh trượt!`;
    return {
      player,
      combat: { ...combat, log: [line, ...combat.log].slice(0, 8) },
      killed: false,
      dodged: false,
      missed: true,
      skipped: false,
    };
  }

  if (rollDodge(player.stats, rng)) {
    const line = `Bạn né đòn của ${enemy.name}!`;
    return {
      player,
      combat: { ...combat, log: [line, ...combat.log].slice(0, 8) },
      killed: false,
      dodged: true,
      missed: false,
      skipped: false,
    };
  }

  const element = enemy.element ?? 'physical';
  if (hasElementalImmunity(combat.playerStatuses, element)) {
    const line = `${enemy.name} đánh trúng nhưng miễn nhiễm nguyên tố chặn đòn!`;
    return {
      player,
      combat: { ...combat, log: [line, ...combat.log].slice(0, 8) },
      killed: false,
      dodged: false,
      missed: false,
      skipped: false,
    };
  }

  if (hasStatus(combat.playerStatuses, 'invulnerable')) {
    const line = `${enemy.name} đánh trúng nhưng Bất tử hấp thụ đòn!`;
    return {
      player,
      combat: { ...combat, log: [line, ...combat.log].slice(0, 8) },
      killed: false,
      dodged: false,
      missed: false,
      skipped: false,
    };
  }

  const line = `${enemy.name} đánh trúng. Với 1 HP, bạn gục xuống.`;
  return {
    player: { ...player, hp: 0, alive: false },
    combat: { ...combat, log: [line, ...combat.log].slice(0, 8) },
    killed: true,
    dodged: false,
    missed: false,
    skipped: false,
  };
}
